import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { debouncedSearch$, SearchApiService } from '@cnt-workspace/data-access';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, combineLatestWith, map } from 'rxjs/operators';

/** A merged listing/boondock/POI suggestion row. Unlike the navbar's own
 *  autocomplete (which drops lat/lon for listing rows because it navigates
 *  straight to the listing detail page), every row here always carries
 *  coordinates — the hero never navigates away directly, so both the
 *  destination-mode /search hand-off and the roadtrip trip-stop builder
 *  need a lat/lon regardless of which result kind was picked. */
export interface IHeroLocationResult {
  /** Set only for a real listing/boondock search hit — never for a POI,
   *  even though POI results carry their own id server-side (a road-trip
   *  stop treats every POI as a "custom" point, per product decision). */
  id?: number;
  isBoondock?: boolean;
  name: string;
  city?: string;
  state?: string;
  lat: number;
  lon: number;
}

let heroLocationSeq = 0;

@Component({
  selector: 'cnt-hero-location-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './hero-location-input.component.html',
  styles: [':host { display: contents; }'],
})
export class HeroLocationInputComponent implements OnDestroy {
  private searchSvc = inject(SearchApiService);
  private elementRef = inject(ElementRef<HTMLElement>);

  private readonly MIN_QUERY_LENGTH = 3;

  @Input() label = '';
  /** Unique per instance: the hero renders up to three of these, and the
   *  label / listbox / option ids must not collide. */
  readonly uid = `hero-loc-${++heroLocationSeq}`;
  @Input() placeholder = '';
  @Input() value = '';
  @Output() valueChange = new EventEmitter<string>();
  @Output() selectionChange = new EventEmitter<IHeroLocationResult | null>();

  results: IHeroLocationResult[] = [];
  open = false;
  activeIndex = -1;

  private readonly terms = new Subject<string>();
  private readonly sub: Subscription;

  constructor() {
    this.sub = debouncedSearch$(this.terms, (term) => {
      if (term.length < this.MIN_QUERY_LENGTH) return of([] as IHeroLocationResult[]);
      const query = { queryTerm: term };
      return this.searchSvc.searchForListingAutocomplete(query).pipe(
        combineLatestWith(this.searchSvc.searchForPoiAutocomplete(query)),
        map(([listingResult, poiResult]) => [
          ...listingResult.results.slice(0, 5).map(
            (r): IHeroLocationResult => ({
              id: r.id,
              isBoondock: r.type === 'boondock' || (r.type === 'standard' && !r.price),
              name: r.name,
              city: r.city,
              state: r.state,
              lat: r.location.lat,
              lon: r.location.lon,
            }),
          ),
          // Populated places only. The POI index also holds individual amenity
          // and activity points, and their full_name often contains the nearby
          // town — so typing "Moab" returned "Moab #341, UT", a numbered site,
          // beside the actual town (Batch A #8). Nothing in the rendered row
          // distinguished them: the icon is a binary id-or-not, so every POI row
          // looked identical.
          //
          // feature_code is the discriminator and the server already sends it.
          // PPL* is the GeoNames family for populated places (PPL, PPLA, PPLC…);
          // POI_* covers dump stations, water, propane, rest areas and
          // activities, which are not destinations a guest searches by name.
          ...poiResult.results
            .filter((r) => r.feature_code?.startsWith('PPL'))
            .slice(0, 5)
            .map(
              (r): IHeroLocationResult => ({
                name: r.name,
                state: r.state,
                lat: r.location.lat,
                lon: r.location.lon,
              }),
            ),
        ]),
        catchError(() => of([] as IHeroLocationResult[])),
      );
    }).subscribe((results) => {
      this.results = results;
      this.open = results.length > 0;
      this.activeIndex = -1;
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onInputChange(next: string): void {
    this.value = next;
    this.valueChange.emit(next);
    this.selectionChange.emit(null);
    this.activeIndex = -1;
    this.terms.next(next.trim());
  }

  onFocus(): void {
    if (this.results.length > 0) this.open = true;
  }

  selectResult(result: IHeroLocationResult): void {
    this.value = result.name;
    this.valueChange.emit(result.name);
    this.selectionChange.emit(result);
    this.open = false;
  }

  onKeydown(e: KeyboardEvent): void {
    if (!this.open || this.results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.activeIndex = (this.activeIndex + 1) % this.results.length;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.activeIndex = (this.activeIndex - 1 + this.results.length) % this.results.length;
    } else if (e.key === 'Enter' && this.activeIndex >= 0) {
      e.preventDefault();
      this.selectResult(this.results[this.activeIndex]);
    } else if (e.key === 'Escape') {
      this.open = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }
}
